import { LAYOUT_SCHEMA_VERSION } from "./grid";
import { GRID_COLUMNS } from "./layoutConfig";
import type {
    DashboardLayoutResponse,
    LayoutItem,
    SignalsDashboardLayoutV1,
} from "./types";
import { safeParseLayout } from "./validation";

export type LayoutSnapshot = {
    layout: SignalsDashboardLayoutV1;
};

/**
 * Cards a save added to the layout or dropped from it, because someone else
 * added or deleted them after this page loaded.
 */
export type LayoutMerge = {
    addedObjectIds: string[];
    removedObjectIds: string[];
};

/**
 * What a save stored. `orders` is every card's order as the server derived it
 * from the layout. `merge` is set only when the server changed the cards.
 */
export type LayoutSaveResult = LayoutSnapshot & {
    orders: Record<string, number>;
    merge?: LayoutMerge;
};

/**
 * Where layouts are stored. The contract is
 * SIGNALS-DASHBOARD-LAYOUT-CONTRACT.md at the repository root.
 */
export interface LayoutPersistence {
    save(
        dashboardId: string,
        snapshot: LayoutSnapshot,
    ): Promise<LayoutSaveResult>;
}

/** The calls the API persistence needs, so tests can stand in for the server. */
export type LayoutApi = {
    updateDashboardLayout(request: {
        dashboardId: string;
        schemaVersion: number;
        columns: number;
        items: LayoutItem[];
    }): Promise<DashboardLayoutResponse>;
};

/** A layout response as a snapshot, or null when it cannot be read. */
export function snapshotFromResponse(
    response: Pick<DashboardLayoutResponse, "items"> & {
        schemaVersion?: number;
    },
): LayoutSnapshot | null {
    const layout = safeParseLayout({
        schemaVersion: response.schemaVersion ?? LAYOUT_SCHEMA_VERSION,
        columns: GRID_COLUMNS,
        items: response.items,
    });
    return layout ? { layout } : null;
}

/**
 * Layouts stored on the server. A dashboard's layout arrives with the
 * dashboard itself, so only saving makes a request.
 */
export class ApiLayoutPersistence implements LayoutPersistence {
    constructor(private readonly api: LayoutApi) {}

    /**
     * Always succeeds unless the request fails: the server drops cards deleted
     * since this page loaded and appends cards added since, then stores the
     * result, so the last save wins.
     */
    async save(
        dashboardId: string,
        snapshot: LayoutSnapshot,
    ): Promise<LayoutSaveResult> {
        const response = await this.api.updateDashboardLayout({
            dashboardId,
            schemaVersion: snapshot.layout.schemaVersion,
            columns: snapshot.layout.columns,
            items: snapshot.layout.items,
        });

        const accepted = snapshotFromResponse(response);
        if (!accepted) {
            throw new Error("The server returned an unreadable layout.");
        }

        const saved = { ...accepted, orders: response.orders };
        const { addedObjectIds, removedObjectIds } = response;
        if (addedObjectIds.length === 0 && removedObjectIds.length === 0) {
            return saved;
        }
        return { ...saved, merge: { addedObjectIds, removedObjectIds } };
    }
}
